import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslate } from "src/locales";
import {
  requestService,
  SelectServiceSimpleRequestDto,
  ConfirmSelectionRequestDto,
  PatientRequestHistoryItemDto,
  GetTestTimesRequestDto,
  GetTestTimesResponseDto,
  FindServicePricesMultiRequestDto,
} from "src/api/request/requestService";
import { enqueueSnackbar } from "notistack";
import { resolveWillOpenCards } from "../../helpers/willOpenCardHelper";

interface FlowActions {
  setWillOpenCardFlowValid: (val: boolean) => void;
  setPendingWillOpenCards: (cards: any[]) => void;
  setActiveWillOpenCardIndex: (idx: number | null) => void;
  setPendingConfirmSelection: (data: any) => void;
  setActiveWillOpenCardServiceName: (name: string | undefined) => void;
  openModalForCard: (card: any, confirmData: any, index: number) => void;
  removeAddInfoForService: (serviceId: number | string) => void;
}

export const useRequestServiceSelection = (
  rowID: string,
  selectedSubCategory: string,
  currentView: string,
  mainSearchTerm: string,
  selectedOfficeId: number,
  flowActions: FlowActions,
  selectedServices: (number | string)[],
  setSelectedServices: React.Dispatch<React.SetStateAction<(number | string)[]>>,
  selectedHistoryServices: (number | string)[],
  setSelectedHistoryServices: React.Dispatch<React.SetStateAction<(number | string)[]>>,
  pendingConfirmSelection: any,
  pendingWillOpenCards: any[]
) => {
  const { t } = useTranslate();
  // selectedServices state lifted to props
  
  // selectedHistoryServices state lifted to props
  const [selectedServicesData, setSelectedServicesData] = useState<any[]>([]);
  const [selectedHistoryServicesData, setSelectedHistoryServicesData] =
    useState<any[]>([]);
  const [categoryServices, setCategoryServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [isServiceProcessing, setIsServiceProcessing] = useState<boolean>(false);
  const [pendingServiceQueue, setPendingServiceQueue] = useState<any[]>([]);
  const [favoriteServiceIds, setFavoriteServiceIds] = useState<Set<number>>(
    new Set()
  );
  const [serviceDataCache, setServiceDataCache] = useState<Map<string, any>>(
    new Map()
  );
  const [testTimes, setTestTimes] = useState<Map<number, string>>(new Map());


  const [historyApiData, setHistoryApiData] = useState<
    PatientRequestHistoryItemDto[]
  >([]);
  const [loadingHistoryData, setLoadingHistoryData] = useState(false);
  const [expandedHistoryRows, setExpandedHistoryRows] = useState<string[]>([]);
  const [historyParentPage, setHistoryParentPage] = useState(0);
  const [historyParentPageSize, setHistoryParentPageSize] = useState(5);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOperationData, setSelectedOperationData] = useState<{
    id: number;
    name: string;
    note: string;
    quantity: number;
  } | null>(null);



  // --- Helpers ---

  const getCurrentOrId = useCallback(() => {
    if (selectedSubCategory && selectedSubCategory.startsWith("api-")) {
      const parts = selectedSubCategory.split("-");
      return parseInt(parts[1]);
    }
    return null;
  }, [selectedSubCategory]);

  const getServiceData = useCallback((id: number | string) => {
    const cacheKey = `service-${id}`;
    const cachedData = serviceDataCache.get(cacheKey);

    let service = categoryServices.find((s) => s.id === id);

    const processId =
      cachedData?.process_id || service?.process_id || (service?.id as number) || (typeof id === 'number' ? id : Number(id));
    const testTime = processId ? testTimes.get(processId) || "" : "";

    const serviceData = {
      id,
      name: service?.name || cachedData?.name || "",
      note: cachedData?.note || "",
      duration: testTime || cachedData?.duration || "",
      total: cachedData?.total || "",
      quantity: cachedData?.quantity || 1,
      orId: cachedData?.orId || null,
      process_id: processId,
    };

    if (service?.name) {
      setServiceDataCache((prev) => new Map(prev).set(cacheKey, serviceData));
    }

    return serviceData;
  }, [categoryServices, serviceDataCache, testTimes]);

  // --- API Fetchers ---

  const fetchCategoryServices = useCallback(async (
    rdmIds: number | number[],
    orId: number
  ) => {
    try {
      setLoadingServices(true);
      const idsArray = Array.isArray(rdmIds) ? rdmIds : [rdmIds];
      const response = await requestService.getRequestListByRdmIds({
        rdmIds: idsArray,
        orId: orId,
      });

      const mapCategoryServices = (data: any[]) => {
        return data.map((item: any) => ({
          ...item,
          id: item.ID || item.id,
          process_id: item.PROCESS_ID || item.process_id,
          name: item.NAME || item.name,
          rdId: item.ID || item.id,
        }));
      };

      if (response && response.data && Array.isArray(response.data)) {
        setCategoryServices(mapCategoryServices(response.data));
      } else if (Array.isArray(response)) {
        setCategoryServices(mapCategoryServices(response));
      }

      try {
        const favoritesResponse = await requestService.getFavorites(orId);
        if (
          favoritesResponse &&
          favoritesResponse.data &&
          Array.isArray(favoritesResponse.data)
        ) {
          const favoriteIds = new Set(
            favoritesResponse.data
              .map((favorite: any) => favorite.rd_id)
              .filter((id: any) => id !== undefined && id !== null)
          );
          setFavoriteServiceIds(favoriteIds);
        }
      } catch (favoritesError) {
        console.error("Error fetching favorites:", favoritesError);
      }
    } catch (error) {
      console.error("Error fetching category services:", error);
    } finally {
      setLoadingServices(false);
    }
  }, []);

  const fetchFavorites = useCallback(async (orId?: number) => {
    try {
      setLoadingServices(true);
      const response = await requestService.getFavorites(orId);

      if (response && response.data && Array.isArray(response.data)) {
        const favoritesData = response.data.map((favorite: any) => ({
          id: favorite.id,
          name: favorite.name,
          description: favorite.note || "",
          category: "Favori",
          isParent: false,
          process_id: favorite.process_id,
          orId: favorite.or_id,
          rdId: favorite.rd_id,
          addedDate: new Date().toISOString(),
        }));
        setCategoryServices(favoritesData);

        const favoriteIds = new Set(
          response.data
            .map((favorite: any) => favorite.rd_id)
            .filter((id: any) => id !== undefined && id !== null)
        );
        setFavoriteServiceIds(favoriteIds);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
      enqueueSnackbar("Favoriler yüklenirken hata oluştu", { variant: "error" });
    } finally {
      setLoadingServices(false);
    }
  }, [t, enqueueSnackbar]);

  const fetchPatientHistory = useCallback(async (patientId: number) => {
    try {
      setLoadingHistoryData(true);
      const response = await requestService.getPatientRequestHistory({
        ptId: patientId,
        timedays: 90,
      });

      if (response && response.data && Array.isArray(response.data)) {
        setHistoryApiData(response.data);
      }
    } catch (error) {
      console.error("Error fetching patient history:", error);
      enqueueSnackbar("Hasta geçmişi yüklenirken hata oluştu", {
        variant: "error",
      });
    } finally {
      setLoadingHistoryData(false);
    }
  }, [enqueueSnackbar]);

  // --- Handlers ---

  const handleServiceToggle = useCallback(async (
    serviceId: number | string,
    explicitService?: any,
    associatedHistoryId?: number | string
  ) => {
    if (isServiceProcessing) return;

    setIsServiceProcessing(true);
    try {
      const service =
        explicitService || categoryServices.find((s) => s.id === serviceId);
      
      // Determine canonical ID (process_id preferred)
      // This ensures we store the same ID regardless of whether it came from Favorites (RecordID) or All (RD_ID)
      const processIdRaw = service?.process_id || service?.PROCESS_ID;
      const canonicalId = processIdRaw || service?.id || serviceId;

      const isAdding = !selectedServices.includes(canonicalId);
      const processId = processIdRaw || (service?.id as number);

      if (!isAdding) {
        setSelectedServices((prev) => prev.filter((id) => id !== canonicalId));

        if (processId) {
          setTestTimes((prev) => {
            const newTestTimes = new Map(prev);
            newTestTimes.delete(processId);
            return newTestTimes;
          });
        }
        // Also remove using serviceId in case add info was stored with it? 
        // Ideally we should use canonicalId there too, but let's clear both to be safe or just canonical.
        flowActions.removeAddInfoForService(canonicalId);
        flowActions.removeAddInfoForService(serviceId); 
        return;
      }

      const requestData: SelectServiceSimpleRequestDto = {
        ptid: parseInt(rowID),
        processid: processId || 0,
        officeid: selectedOfficeId || 0,
        isselected: isAdding,
        quantity: 1,
        note: "",
      };

      const response = await requestService.selectServiceSimple(requestData);

      if (!response.issuccessful) {
        console.error("Hizmet seçimi başarısız:", response.errormessage);
        enqueueSnackbar(response.errormessage || "Hizmet seçilemedi", {
          variant: "error",
        });
        return;
      }

      const rawWillOpenCard =
        (response as any).willopencard ?? response.validationresult?.willopencard;
      const incomingCards = resolveWillOpenCards(rawWillOpenCard);

      if (incomingCards.length) {
        // Cache the service data first so that after selection it has a name
        const serviceNameForCards =
          (service as any)?.name ||
          (service as any)?.NAME ||
          (service as any)?.process_name ||
          undefined;

        const currentOrId = getCurrentOrId();
        if (currentOrId || processId) {
            const cacheKey = `service-${canonicalId}`;
            setServiceDataCache((prevCache) => {
              const newCache = new Map(prevCache);
              const existingData = newCache.get(cacheKey) || {};
              newCache.set(cacheKey, {
                ...existingData,
                name: serviceNameForCards || existingData.name || "",
                note: service?.note || existingData.note || "",
                orId: currentOrId ?? existingData.orId,
                processId: processId ?? existingData.processId,
              });
              return newCache;
            });
        }
        
        flowActions.setWillOpenCardFlowValid(true);
        flowActions.setActiveWillOpenCardServiceName(serviceNameForCards);
        flowActions.setPendingWillOpenCards(incomingCards);
        flowActions.setActiveWillOpenCardIndex(0);

        const pendingConfirm = {
          ptId: parseInt(rowID),
          processId: processId || 0,
          isSelected: isAdding,
          quantity: 1,
          officeId: selectedOfficeId || undefined,
          serviceId: canonicalId, // Store canonical ID for confirmation completion
          historyGridId: associatedHistoryId,
        };
        flowActions.setPendingConfirmSelection(pendingConfirm);
        flowActions.openModalForCard(incomingCards[0], pendingConfirm, 0);
        return;
      }

      try {
        const confirmRequest: ConfirmSelectionRequestDto = {
          ptId: parseInt(rowID),
          processId: processId || 0,
          isSelected: isAdding,
          quantity: 1,
          officeId: selectedOfficeId || undefined,
        };

        const confirmResponse = await requestService.confirmSelection(
          confirmRequest
        );
        const isConfirmed =
          confirmResponse.isSuccess ||
          (confirmResponse as any).issuccess ||
          (confirmResponse as any).issuccessful;

        if (isConfirmed) {
          enqueueSnackbar(
            t("patient-request-service.messages.service-selected"),
            { variant: "success" }
          );
          setSelectedServices((prev) => [...prev, canonicalId]);

          const currentOrId = getCurrentOrId();
          if (currentOrId || processId) {
            const cacheKey = `service-${canonicalId}`;
            setServiceDataCache((prevCache) => {
              const newCache = new Map(prevCache);
              const existingData = newCache.get(cacheKey) || {};
              newCache.set(cacheKey, {
                ...existingData,
                name: service?.name || (service as any)?.NAME || (service as any)?.process_name || existingData.name || "",
                note: service?.note || existingData.note || "",
                orId: currentOrId ?? existingData.orId,
                processId: processId ?? existingData.processId,
              });
              return newCache;
            });
          }
        }
      } catch (confirmError) {
        console.error("Confirm Selection Error:", confirmError);
        enqueueSnackbar("Hizmet seçimi onaylanırken hata oluştu", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Service toggle error", error);
      enqueueSnackbar("Hizmet seçimi sırasında hata", { variant: "error" });
    } finally {
      setIsServiceProcessing(false);
    }
  }, [
    isServiceProcessing,
    selectedServices,
    categoryServices,
    rowID,
    selectedOfficeId,
    flowActions,
    t,
    getCurrentOrId,
    setSelectedServices
  ]);


  // Process Pending Queue
  useEffect(() => {
    if (
      pendingServiceQueue.length > 0 && 
      !isServiceProcessing &&
      !pendingConfirmSelection &&
      pendingWillOpenCards.length === 0
    ) {
      const processQueue = async () => {
        const item = pendingServiceQueue[0];
        
        await handleServiceToggle(
          item.serviceId, 
          item.explicitService, 
          item.associatedHistoryId
        );
        
        setPendingServiceQueue((prev) => prev.slice(1));
      };
      processQueue();
    }
  }, [pendingServiceQueue, isServiceProcessing, handleServiceToggle, pendingConfirmSelection, pendingWillOpenCards]);

  const handleAddToFavorites = async (rdId: number, serviceName: string) => {
    try {
      const result = await requestService.addToFavorites(rdId);
      if (result.data) {
        setFavoriteServiceIds((prev) => new Set(Array.from(prev).concat(rdId)));
        enqueueSnackbar(`${serviceName} sık kullanılanlara eklendi`, {
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error adding to favorites:", error);
      enqueueSnackbar("Sık kullanılanlara eklenirken hata oluştu", {
        variant: "error",
      });
    }
  };

  const handleRemoveFromFavorites = async (id: number, serviceName: string) => {
    try {
      const result = await requestService.removeFromFavorites(id);
        if (result.data) {
        const service = categoryServices.find((s) => s.id === id);
        if (service) {
          const serviceIdToRemove = service.rdId;
          if (serviceIdToRemove) {
            setFavoriteServiceIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(serviceIdToRemove);
              return newSet;
            });
            
            // If current view is frequent, remove from grid immediately
            if (currentView === "frequent") {
                setCategoryServices((prev) => prev.filter((s) => s.id !== id));
            }
          }
        }
        enqueueSnackbar(`${serviceName} sık kullanılanlardan çıkarıldı`, {
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error removing from favorites:", error);
      enqueueSnackbar("Sık kullanılanlardan çıkarılırken hata oluştu", {
        variant: "error",
      });
    }
  };

  // Wrappers to handle find-and-call logic for Grid Area props (which only pass ID)
  const handleAddToFrequent = async (serviceId: number | string) => {
        const service = categoryServices.find((s) => s.id === serviceId);
        const rdId = service?.id || serviceId;
        await handleAddToFavorites(
          rdId as number,
          service?.name || `Servis ${serviceId}`
        );
  };

  const handleRemoveFromFrequent = async (serviceId: number | string) => {
      // Logic assumes finding by ID in favorites list or category list
      const service = categoryServices.find((cs) => cs.id === serviceId);
      const serviceName = service ? service.name || service.NAME : "Servis";
      const favoriteRecordId = Number(serviceId);

      if (favoriteRecordId) {
        await handleRemoveFromFavorites(favoriteRecordId, serviceName);
      }
  };

  // --- Effects ---

  useEffect(() => {
    const newData = selectedServices.map((id) => getServiceData(id));
    setSelectedServicesData(newData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices, serviceDataCache, testTimes, getServiceData]);

  // History Data Transformation
    const transformHistoryData = (apiData: PatientRequestHistoryItemDto[]) => {
    const groupedData = apiData.reduce((acc, item) => {
      const category = item.kart_adi || "Diğer";
      item.dates.forEach((date) => {
        const formattedDate = new Date(date).toLocaleDateString("tr-TR");
        const groupKey = `${category}_${formattedDate}`;
        if (!acc[groupKey]) {
          acc[groupKey] = {
            category,
            date: formattedDate,
            items: [],
          };
        }
        acc[groupKey].items.push(item);
      });
      return acc;
    }, {} as Record<string, { category: string; date: string; items: PatientRequestHistoryItemDto[] }>);

    let parentId = 1;
    const result = Object.values(groupedData).map((group) => {
      const currentParentId = parentId++;
      return {
        id: currentParentId,
        requestDate: group.date,
        operationName: `${group.category} - ${group.date}`,
        quantity: "",
        note: "",
        isParent: true,
        children: group.items.map((item, index) => ({
          id: currentParentId * 100 + index + 1,
          requestDate: group.date,
          operationName: item.process_name,
          quantity: "1",
          note: item.note || "",
          isParent: false,
          originalData: item,
        })),
      };
    });
    return result;
  };

  const historyData = useMemo(() => {
    if (historyApiData.length > 0) {
      return transformHistoryData(historyApiData);
    }
    return [];
  }, [historyApiData]);

  // Sadece parent'ları sayarak pagination yap
  const startParentIndex = historyParentPage * historyParentPageSize;
  const endParentIndex = startParentIndex + historyParentPageSize;
  const visibleParents = historyData.slice(startParentIndex, endParentIndex);

  // Treeview data'yı düzleştir
  // Logic for flattenedHistoryData
  const flattenedHistoryData = useMemo(() => {
    const searchLower = mainSearchTerm.toLowerCase().trim();

    return visibleParents.reduce((acc: any[], parent) => {
      let shouldIncludeParent = false;
      const matchingChildren: any[] = [];

      // Check if parent matches search
      if (searchLower) {
        const parentName = parent.operationName || "";
        const parentNote = parent.note || "";
        if (
          parentName.toLowerCase().includes(searchLower) ||
          parentNote.toLowerCase().includes(searchLower)
        ) {
          shouldIncludeParent = true;
        }
      } else {
        shouldIncludeParent = true; 
      }

      // Check children for matches
      if (parent.children) {
        parent.children.forEach((child: any) => {
          if (searchLower) {
            const childName = child.operationName || "";
            const childNote = child.note || "";
            const processName = child.originalData?.process_name || "";

            if (
              childName.toLowerCase().includes(searchLower) ||
              childNote.toLowerCase().includes(searchLower) ||
              processName.toLowerCase().includes(searchLower)
            ) {
              matchingChildren.push(child);
              shouldIncludeParent = true; 
            }
          } else {
            if (expandedHistoryRows.includes(parent.id.toString())) {
              matchingChildren.push(child);
            }
          }
        });
      }

      if (shouldIncludeParent) {
        acc.push(parent);
        if (matchingChildren.length > 0) {
          acc.push(...matchingChildren);
        }
      }

      return acc;
    }, []);
  }, [visibleParents, expandedHistoryRows, mainSearchTerm]);


  // HISTORY TOGGLE
  const handleHistoryServiceToggle = (serviceId: number | string, originalData?: any) => {
    const realServiceId = originalData?.process_id || originalData?.id || serviceId;
    
    // Check if this process_id is already selected in regular selections (not history)
    const isAlreadyInRegularSelections = selectedServicesData.some(
      (s: any) => s.process_id === realServiceId
    );
    
    // If trying to add (not remove) and already in regular selections, show warning
    const isCurrentlySelectedInHistory = selectedHistoryServices.includes(serviceId);
    if (!isCurrentlySelectedInHistory && isAlreadyInRegularSelections) {
      enqueueSnackbar("Bu hizmet zaten seçili listesinde mevcut", {
        variant: "warning",
        autoHideDuration: 3000,
      });
      return;
    }
    
    const explicitService = originalData ? {
        id: realServiceId,
        name: originalData.process_name || originalData.name,
        code: originalData.code,
        st_name: originalData.st_name,
        rt_name: originalData.rt_name,
        process_id: realServiceId,
    } : undefined;

    handleServiceToggle(realServiceId, explicitService, serviceId); 
    
    setSelectedHistoryServices((prev) => 
        prev.includes(serviceId)
            ? prev.filter((s) => s !== serviceId)
            : [...prev, serviceId]
    );
  };
  
    const handleHistoryParentToggle = (parentId: number | string) => {
    const parent = historyData.find((h: any) => h.id === parentId);
    if (!parent || !parent.children) return;

    const childIds: (number | string)[] = parent.children.map(
      (child: any) => child.id
    );

    const allChildrenSelected = childIds.every((id) =>
      selectedHistoryServices.includes(id)
    );

    if (allChildrenSelected) {
      // Deselect all logic
      const itemsToQueue = childIds
        .filter((id) => selectedHistoryServices.includes(id))
        .map((id) => {
          const child = parent.children?.find((c: any) => c.id === id);
          return {
            serviceId: child?.originalData?.process_id || child?.originalData?.id || id,
            explicitService: child?.originalData
              ? {
                  id:
                    child.originalData.process_id ||
                    child.originalData.id ||
                    id,
                  name:
                    child.originalData.process_name ||
                    (child.originalData as any).name ||
                    "",
                  code: (child.originalData as any).code || "",
                  st_name: (child.originalData as any).st_name || "",
                  rt_name: (child.originalData as any).rt_name || "",
                  process_id:
                    child.originalData.process_id ||
                    child.originalData.id ||
                    id,
                }
              : undefined,
            associatedHistoryId: id,
          };
        });
      setPendingServiceQueue((prev) => [...prev, ...itemsToQueue]);
      
      // Update UI state immediately
      setSelectedHistoryServices((prev) => 
        prev.filter((id) => !childIds.includes(id))
      );

    } else {
      // Select all logic
      const itemsToQueue = childIds
        .filter((id) => !selectedHistoryServices.includes(id))
        .map((id) => {
          const child = parent.children?.find((c: any) => c.id === id);
          return {
            serviceId: child?.originalData?.process_id || child?.originalData?.id || id,
            explicitService: child?.originalData
              ? {
                  id:
                    child.originalData.process_id ||
                    child.originalData.id ||
                    id,
                  name:
                    child.originalData.process_name ||
                    (child.originalData as any).name ||
                    "",
                  code: (child.originalData as any).code || "",
                  st_name: (child.originalData as any).st_name || "",
                  rt_name: (child.originalData as any).rt_name || "",
                  process_id:
                    child.originalData.process_id ||
                    child.originalData.id ||
                    id,
                }
              : undefined,
            associatedHistoryId: id,
          };
        });
      setPendingServiceQueue((prev) => [...prev, ...itemsToQueue]);

      // Update UI state immediately
      setSelectedHistoryServices((prev) => {
        const newIds = childIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };
  
  const handleParentToggle = (categoryId: string) => {
       if (expandedHistoryRows.includes(categoryId)) {
          setExpandedHistoryRows(
            expandedHistoryRows.filter((id) => id !== categoryId)
          );
        } else {
          setExpandedHistoryRows([...expandedHistoryRows, categoryId]);
        }
  };

  const handleModalClose = () => {
    setSelectedServices([]);
    setSelectedHistoryServices([]);
    setSelectedServicesData([]);
    setSelectedHistoryServicesData([]);
    setCategoryServices([]);
    setExpandedHistoryRows([]);
    setServiceDataCache(new Map());
    setTestTimes(new Map());
    setFavoriteServiceIds(new Set());
    setHistoryApiData([]);
    setHistoryParentPage(0);
    setEditModalOpen(false);
    setSelectedOperationData(null);
    setPendingServiceQueue([]); 
  };
  
  // Others
  const handleSelectAllSelected = () => {
        if (currentView === "history") {
          if (
            selectedHistoryServices.length === (currentSelectedServicesData?.length || 0)
          ) {
            setSelectedHistoryServices([]);
          } else {
             // Logic simplified
            setSelectedHistoryServices(
              (currentSelectedServicesData || []).map((s: any) => s.id)
            );
          }
        } else {
          if (selectedServices.length === (currentSelectedServicesData?.length || 0)) {
            setSelectedServices([]);
          } else {
            setSelectedServices((currentSelectedServicesData || []).map((s: any) => s.id));
          }
        }
  };

   const handleEditOperation = (operationData: any) => {
    setSelectedOperationData(operationData);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedOperationData(null);
  };

  const handleUpdateOperation = (updatedData: {
    id: number;
    note: string;
    quantity: number;
  }) => {
    const serviceCacheKey = `service-${updatedData.id}`;
    const historyCacheKey = `history-${updatedData.id}`;

    setServiceDataCache((prev) => {
      const newCache = new Map(prev);
      const serviceData = newCache.get(serviceCacheKey);
      const historyData = newCache.get(historyCacheKey);

      if (serviceData) {
        newCache.set(serviceCacheKey, {
          ...serviceData,
          note: updatedData.note,
          quantity: updatedData.quantity,
        });
      }
      if (historyData) {
        newCache.set(historyCacheKey, {
          ...historyData,
          note: updatedData.note,
          quantity: updatedData.quantity,
        });
      }

      return newCache;
    });

    setSelectedServicesData((prev) => {
      return prev.map((service) =>
        service.id === updatedData.id
          ? {
              ...service,
              note: updatedData.note,
              quantity: updatedData.quantity,
            }
          : service
      );
    });

    setSelectedHistoryServicesData((prev) => {
      return prev.map((service) =>
        service.id === updatedData.id
          ? {
              ...service,
              note: updatedData.note,
              quantity: updatedData.quantity,
            }
          : service
      );
    });

    enqueueSnackbar("Hizmet bilgileri başarıyla güncellendi", {
      variant: "success",
      autoHideDuration: 3000,
    });
  };
  
  // Format price to Turkish currency format (1350 -> 1.350,00)
  const formatPrice = (price: number): string => {
    if (isNaN(price) || price === 0) {
      return "0,00";
    }

    return new Intl.NumberFormat("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  // Fetch test times for selected services
  const handleTestDurationsClick = async () => {
    try {
      // Use combined data from both regular and history selections, deduplicated by process_id
      // Regular selections take priority (user may have edited quantity)
      const dedupeMap = new Map<number | string, any>();
      selectedServicesData.forEach((s: any) => {
        const pid = s.process_id || s.id;
        if (pid) dedupeMap.set(pid, s);
      });
      selectedHistoryServicesData.forEach((s: any) => {
        const pid = s.process_id || s.originalData?.process_id || s.id;
        if (pid && !dedupeMap.has(pid)) dedupeMap.set(pid, s);
      });
      const allSelectedData = Array.from(dedupeMap.values());

      if (allSelectedData.length === 0) {
        enqueueSnackbar("Lütfen önce hizmet seçin", {
          variant: "warning",
          autoHideDuration: 3000,
        });
        return;
      }

      // Extract process IDs from all selected services
      const processIds = allSelectedData
        .map((service: any) => {
          return (
            service.process_id ||
            service.originalData?.process_id ||
            service.id
          );
        })
        .filter((id: any) => id !== undefined);

      if (processIds.length === 0) {
        enqueueSnackbar("Seçilen hizmetlerde process_id bulunamadı", {
          variant: "error",
          autoHideDuration: 3000,
        });
        return;
      }

      // Prepare request
      const request: GetTestTimesRequestDto = {
        rd_ids: processIds,
        off_id: selectedOfficeId,
      };

      // Call API
      const response = await requestService.getTestTimes(request);

      if (response && response.data) {
        // Create mapping from process_id to running_time
        const timesMap = new Map<number, string>();
        response.data.forEach((item: GetTestTimesResponseDto) => {
          timesMap.set(item.process_id, item.running_time);
        });

        setTestTimes(timesMap);

        // Update both regular and history services data with test times
        const updatedServicesData = selectedServicesData.map((service) => {
          const processId = service.process_id;
          const testTime = processId ? timesMap.get(processId) || "" : "";
          return {
            ...service,
            duration: testTime,
          };
        });
        setSelectedServicesData(updatedServicesData);

        const updatedHistoryData = selectedHistoryServices.map((id) => {
          const historyService =
            flattenedHistoryData.find((h: any) => h.id === id) ||
            historyData
              .flatMap((p: any) => p.children || [])
              .find((c: any) => c.id === id);

          const processId =
            historyService?.originalData?.process_id ||
            historyService?.originalData?.id;
          const testTime = processId ? timesMap.get(processId) || "" : "";

          const existingHistoryService = selectedHistoryServicesData.find(
            (s) => s.id === id
          );

          return {
            id,
            name: historyService?.operationName || "",
            note: historyService?.note || "",
            duration: testTime,
            total: existingHistoryService?.total || "",
            quantity: historyService?.quantity || 1,
            process_id: processId,
            originalData: historyService?.originalData,
          };
        });
        setSelectedHistoryServicesData(updatedHistoryData);

        enqueueSnackbar("Test süreleri başarıyla yüklendi", {
          variant: "success",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching test times:", error);
      enqueueSnackbar("Test süreleri yüklenirken hata oluştu", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  // Fetch patient debt for selected services
  const handlePatientDebtClick = async () => {
    try {
      // Use combined data from both regular and history selections, deduplicated by process_id
      // Regular selections take priority (user may have edited quantity)
      const dedupeMap = new Map<number | string, any>();
      selectedServicesData.forEach((s: any) => {
        const pid = s.process_id || s.id;
        if (pid) dedupeMap.set(pid, s);
      });
      selectedHistoryServicesData.forEach((s: any) => {
        const pid = s.process_id || s.originalData?.process_id || s.id;
        if (pid && !dedupeMap.has(pid)) dedupeMap.set(pid, s);
      });
      const allSelectedData = Array.from(dedupeMap.values());

      if (allSelectedData.length === 0) {
        enqueueSnackbar("Lütfen önce hizmet seçin", {
          variant: "warning",
          autoHideDuration: 3000,
        });
        return;
      }

      // Get patient ID from props
      if (!rowID) {
        enqueueSnackbar("Hasta ID bulunamadı", {
          variant: "error",
          autoHideDuration: 3000,
        });
        return;
      }

      // Prepare services array for API
      const servicesForApi = allSelectedData
        .map((service: any) => {
          const processId =
            service.process_id ||
            service.originalData?.process_id ||
            service.id;

          return {
            serviceid: processId,
            quantity: service.quantity || 1,
          };
        })
        .filter((service: any) => service.serviceid !== undefined);

      if (servicesForApi.length === 0) {
        enqueueSnackbar("Seçilen hizmetlerde geçerli service ID bulunamadı", {
          variant: "error",
          autoHideDuration: 3000,
        });
        return;
      }

      // Prepare request
      const request: FindServicePricesMultiRequestDto = {
        ptid: parseInt(rowID),
        services: servicesForApi,
      };

      // Call API
      const response = await requestService.findServicePricesMulti(request);

      if (response && response.data && response.data.serviceresults) {
        // Create mapping from serviceid to pattotalamount
        const pricesMap = new Map<number, number>();
        response.data.serviceresults.forEach((result: any) => {
          if (result.success && result.servicedetail) {
            pricesMap.set(
              result.serviceid,
              result.servicedetail.pattotalamount
            );
          }
        });

        // Update both regular and history services data with prices
        const updatedServicesData = selectedServicesData.map((service: any) => {
          const processId = service.process_id || service.id;
          const price = pricesMap.get(processId) || 0;

          return {
            ...service,
            total: formatPrice(price),
          };
        });
        setSelectedServicesData(updatedServicesData);

        const updatedHistoryData = selectedHistoryServices.map((id) => {
          const historyService =
            flattenedHistoryData.find((h: any) => h.id === id) ||
            historyData
              .flatMap((p: any) => p.children || [])
              .find((c: any) => c.id === id);

          const processId =
            historyService?.originalData?.process_id ||
            historyService?.originalData?.id;
          const price = processId ? pricesMap.get(processId) || 0 : 0;

          const existingHistoryService = selectedHistoryServicesData.find(
            (s) => s.id === id
          );

          return {
            id,
            name: historyService?.operationName || "",
            note: historyService?.note || "",
            duration: existingHistoryService?.duration || "",
            total: formatPrice(price),
            quantity: historyService?.quantity || 1,
            process_id: processId,
            originalData: historyService?.originalData,
          };
        });
        setSelectedHistoryServicesData(updatedHistoryData);

        enqueueSnackbar("Hasta borcu başarıyla yüklendi", {
          variant: "success",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching patient debt:", error);
      enqueueSnackbar("Hasta borcu yüklenirken hata oluştu", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };
  // Current view's data for grid display
  const currentSelectedServicesData = currentView === "history" ? selectedHistoryServicesData : selectedServicesData;
  
  // Combined data for SelectedServicesPanel - deduplicated, regular selections take priority
  const combinedSelectedServicesData = (() => {
    const dedupeMap = new Map<number | string, any>();
    selectedServicesData.forEach((s: any) => {
      const pid = s.process_id || s.id;
      if (pid) dedupeMap.set(pid, s);
    });
    selectedHistoryServicesData.forEach((s: any) => {
      const pid = s.process_id || s.originalData?.process_id || s.id;
      if (pid && !dedupeMap.has(pid)) dedupeMap.set(pid, s);
    });
    return Array.from(dedupeMap.values());
  })();
  
  // Combined selection IDs for SelectedServicesPanel
  const combinedSelectedItems = [...selectedServices, ...selectedHistoryServices];
  
  // Set of all selected process_ids for duplicate checking
  const allSelectedProcessIds = new Set([
    ...selectedServicesData.map((s: any) => s.process_id).filter(Boolean),
    ...selectedHistoryServicesData.map((s: any) => s.process_id || s.originalData?.process_id).filter(Boolean),
  ]);

  return {
    selectedServices,
    setSelectedServices,
    selectedHistoryServices,
    setSelectedHistoryServices,
    selectedServicesData,
    setSelectedServicesData,
    selectedHistoryServicesData,
    setSelectedHistoryServicesData,
    currentSelectedServicesData, // Exported
    combinedSelectedServicesData, // Both regular and history selections
    combinedSelectedItems, // Combined IDs for selection
    allSelectedProcessIds, // For duplicate checking
    categoryServices,
    setCategoryServices,
    loadingServices,
    setLoadingServices,
    isServiceProcessing,
    setIsServiceProcessing,
    pendingServiceQueue,
    setPendingServiceQueue,
    favoriteServiceIds,
    setFavoriteServiceIds,
    editModalOpen,
    setEditModalOpen,
    selectedOperationData,
    setSelectedOperationData,
    historyApiData,
    setHistoryApiData,
    loadingHistoryData,
    setLoadingHistoryData,
    expandedHistoryRows,
    setExpandedHistoryRows,
    historyParentPage,
    setHistoryParentPage,
    historyParentPageSize,
    setHistoryParentPageSize,
    historyData,
    flattenedHistoryData, // Exported
    
    fetchCategoryServices,
    fetchFavorites,
    fetchPatientHistory,
    handleServiceToggle,
    handleAddToFavorites, // Export as-is (2 args)
    handleRemoveFromFavorites, // Export as-is (2 args)
    handleAddToFrequent, // Exported wrapper (1 arg)
    handleRemoveFromFrequent, // Exported wrapper (1 arg)
    
    handleModalClose, // Exported
    handleHistoryServiceToggle,
    handleHistoryParentToggle,
    handleParentToggle,
    
    handleSelectAllSelected,
    handleEditOperation,
    handleCloseEditModal,
    handleUpdateOperation,
    handleTestDurationsClick,
    handlePatientDebtClick,
  };
};
